/**
 * Hook for managing SSE connection and notes state.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { getNotes, syncTree } from "../lib/api";
import { subscribeToNotes } from "../lib/sse";
import { applyPatch } from "../lib/treeConverter";
import type { NotesState, TreeNode, PatchOp, PatchEvent } from "../lib/types";
import {
  getConnectionStatusForNotesStatus,
  normalizeNotesState,
  recoverConnectionStatusOnStreamOpen,
} from "./notesStreamState";
import type { ConnectionStatus } from "./notesStreamState";

export type { ConnectionStatus } from "./notesStreamState";

export interface UseNotesStreamReturn {
  notes: NotesState | null;
  setNotes: React.Dispatch<React.SetStateAction<NotesState | null>>;
  connectionStatus: ConnectionStatus;
  syncTree: (contactId: string, tree: TreeNode) => Promise<void>;
}

/**
 * Hook for managing SSE connection and notes state.
 *
 * Uses:
 * - REST API for initial data fetch
 * - Server-Sent Events for real-time patch updates
 * - REST API for syncing changes
 *
 * @param contactId - The contact ID to stream notes for
 * @returns { notes, setNotes, connectionStatus, syncTree }
 */
export function useNotesStream(contactId: string | null, streamingEnabled = true): UseNotesStreamReturn {
  const [notes, setNotes] = useState<NotesState | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("disconnected");
  const versionRef = useRef<number>(0);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Track processed versions to avoid duplicate processing
  const processedVersionsRef = useRef<Set<number>>(new Set());

  // Apply patches from subscription
  const applyPatches = useCallback((patchEvent: PatchEvent) => {
    const version = patchEvent.version;

    // Skip if we've already processed this version
    if (processedVersionsRef.current.has(version)) {
      console.log("[SSE] Skipping already processed version:", version);
      return;
    }

    console.log("[SSE] Processing patches for version:", version);
    processedVersionsRef.current.add(version);

    // Parse patches from JSON string if needed
    const patches: PatchOp[] =
      typeof patchEvent.patches === "string"
        ? JSON.parse(patchEvent.patches as unknown as string)
        : patchEvent.patches;

    // Update version
    versionRef.current = version;

    setNotes((prevNotes) => {
      if (!prevNotes) {
        console.log("No prevNotes, skipping patches");
        return prevNotes;
      }

      let newTree = prevNotes.tree;
      let newStatus = prevNotes.status;

      // Apply each patch
      for (const patch of patches) {
        console.log("Applying patch:", patch.op, patch.parentId || patch.nodeId);

        // Handle status.update separately
        if (patch.op === "status.update") {
          newStatus = (patch.status as "live" | "completed") || newStatus;
          console.log("Status updated to:", newStatus);
          continue;
        }

        const localPatch = convertPatchToLocal(patch);
        if (localPatch) {
          newTree = applyPatch(newTree, localPatch);
        }
      }

      console.log("Tree updated to version:", version);
      return { ...prevNotes, tree: newTree, status: newStatus, version };
    });

    // Also update connectionStatus for status.update patches
    for (const patch of patches) {
      if (patch.op === "status.update" && patch.status) {
        setConnectionStatus(patch.status === "completed" ? "completed" : "live");
      }
    }
  }, []);

  const applyIncomingNotesState = useCallback((noteData: NotesState) => {
    const normalizedNotes = normalizeNotesState(noteData);
    setNotes(normalizedNotes);
    versionRef.current = normalizedNotes.version;
    setConnectionStatus(getConnectionStatusForNotesStatus(normalizedNotes.status));
  }, []);

  // Fetch initial data and set up subscription
  useEffect(() => {
    if (!contactId) {
      setNotes(null);
      setConnectionStatus("disconnected");
      return;
    }

    let cancelled = false;
    setConnectionStatus("connecting");

    // Fetch initial note
    async function fetchNote() {
      try {
        const noteData = await getNotes(contactId!);

        if (cancelled) return;

        if (noteData) {
          const normalizedNotes = normalizeNotesState(noteData);
          console.log("[SSE] Parsed tree:", normalizedNotes.tree);
          applyIncomingNotesState(noteData);
        } else {
          // No note exists yet - show empty state
          setNotes(null);
          setConnectionStatus("live");
        }
      } catch (error) {
        console.error("[SSE] Error fetching note:", error);
        if (!cancelled) {
          setConnectionStatus("error");
        }
      }
    }

    fetchNote();
    return () => { cancelled = true; };
  }, [contactId, applyIncomingNotesState]);

  // Keep the loaded document intact when streaming stops for review.
  useEffect(() => {
    if (!contactId || !streamingEnabled) {
      if (contactId) setConnectionStatus("completed");
      return;
    }
    let cancelled = false;

    // Set up subscription for patches
    function setupSubscription() {
      console.log("[DEBUG] setupSubscription called for contactId:", contactId);
      unsubscribeRef.current = subscribeToNotes(contactId!, {
        onOpen: () => {
          if (!cancelled) {
            setConnectionStatus((currentStatus) => recoverConnectionStatusOnStreamOpen(currentStatus));
          }
        },
        onInit: (noteData) => {
          if (!cancelled) {
            applyIncomingNotesState(noteData);
          }
        },
        onPatch: (patchEvent) => {
          console.log("[DEBUG] Received patchData:", patchEvent);
          if (!cancelled) {
            applyPatches(patchEvent);
          }
        },
        onError: (error) => {
          console.error("[DEBUG] Subscription error:", error);
          if (!cancelled) {
            setConnectionStatus("error");
          }
        },
      });
      console.log("[DEBUG] Subscription setup complete for", contactId);
    }

    console.log("[DEBUG] useEffect running, contactId:", contactId);
    setupSubscription();

    return () => {
      cancelled = true;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [contactId, streamingEnabled, applyIncomingNotesState, applyPatches]);

  // Sync tree to backend
  const syncTreeFn = useCallback(async (cId: string, treeRoot: TreeNode) => {
    try {
      const version = versionRef.current + 1;
      const result = await syncTree({
        contact_id: cId,
        tree: treeRoot,
        version,
      });
      versionRef.current = result.version;
      console.log("[SSE] Tree synced, new version:", result.version);
    } catch (error) {
      console.error("[SSE] Error syncing tree:", error);
    }
  }, []);

  return { notes, setNotes, connectionStatus: streamingEnabled ? connectionStatus : "completed", syncTree: syncTreeFn };
}

/**
 * Validate and pass through patch format.
 *
 * Patches from backend are already in the correct format:
 *   { op: "node.add", parentId: "...", node: {...} }
 *   { op: "node.update", nodeId: "...", updates: {...} }
 *   { op: "node.delete", nodeId: "..." }
 */
function convertPatchToLocal(patch: PatchOp): PatchOp | null {
  if (!patch || !patch.op) return null;

  // Validate patch format
  if (
    patch.op === "node.add" ||
    patch.op === "node.update" ||
    patch.op === "node.delete"
  ) {
    return patch;
  }

  // Unknown patch format
  console.warn("[SSE] Unknown patch format:", patch);
  return null;
}
